/**
 * El paso 5: que va a pasar, en numeros. Y el 6: ya paso, con Deshacer a mano.
 *
 * Los totales van por moneda y nunca sumados: 77 dolares y 40.000 pesos no son
 * un numero. Los cambios de precio se listan uno por uno, con el de antes y el
 * de despues, porque es lo que el vendedor le va a decir al cliente manana.
 */
import { useState } from "react";
import { ArrowRight, Undo2 } from "lucide-react";
import { formatearNumero, type Moneda } from "@tora/shared";

import { mostrarCantidad } from "@/components/catalogo/Campos";
import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { formatearFechaHora } from "@/lib/utils";

import {
  SALTO_DE_PRECIO,
  hayPreciosQueSaltan,
  referenciaDe,
  referenciaEnFrase,
  textoDeAplicar,
  type Compra,
  type PuedeAplicar,
  type ResultadoAplicarCompra,
  type ResumenAplicar,
} from "./logica";

export interface RevisionProps {
  compra: Compra;
  resumen: ResumenAplicar;
  puede: PuedeAplicar;
  aplicando: boolean;
  revirtiendo: boolean;
  resultado: ResultadoAplicarCompra | null;
  onVolver: () => void;
  onAplicar: () => void;
  /**
   * El bloque de deuda con el proveedor. Llega como slot y no se arma acá porque
   * necesita las cuentas financieras y el editor de medios de pago, que son de
   * la pantalla. Null cuando la compra ya está aplicada: la deuda ya se asentó.
   */
  deuda?: JSX.Element | null;
  /** Los encargos que trae la factura (0111), para tildar antes de aplicar. */
  encargos?: JSX.Element | null;
  /** Cuantas unidades se tildaron para cerrar un encargo: no quedan en el stock. */
  encargosTildados?: number;
  /** Lineas que entran en otra moneda que la compra: no suman a la deuda. */
  lineasEnOtraMoneda?: number;
  /** Unidades del remito que no llegaron (contadas de menos). */
  unidadesQueNoLlegaron?: number;
  /**
   * La deuda que se anota al aplicar, para decirla en la confirmacion. Antes
   * el dialogo hablaba solo de unidades, y un importe mal tipeado en «Cuánto
   * le debo» se aplicaba sin volver a verse.
   */
  deudaAlAplicar?: {
    importe: number;
    moneda: Moneda;
    proveedor: string;
    conPago: boolean;
    /** Lo que el pago no cubre y queda como deuda. */
    falta: number;
  } | null;
  /** El importe de la deuda es sospechoso y todavia no se confirmo (BloqueDeuda). */
  deudaPorConfirmar?: boolean;
  onRevertir: () => void;
}

export function Revision({
  compra,
  resumen,
  puede,
  aplicando,
  revirtiendo,
  resultado,
  onVolver,
  onAplicar,
  onRevertir,
  deuda = null,
  encargos = null,
  encargosTildados = 0,
  lineasEnOtraMoneda = 0,
  unidadesQueNoLlegaron = 0,
  deudaAlAplicar = null,
  deudaPorConfirmar = false,
}: RevisionProps): JSX.Element {
  // Con el total de la factura cargado, la deuda es ese total (`aplicar_compra`
  // no mira las lineas): lo que no llego o vino en otra moneda ya esta adentro.
  // Los avisos decian que no sumaba aunque el total lo incluyera.
  const conTotal = compra.total_a_pagar !== null || compra.total_declarado !== null;
  const [confirmarAplicar, setConfirmarAplicar] = useState(false);
  const [confirmarRevertir, setConfirmarRevertir] = useState(false);
  const aplicada = compra.estado === "APLICADO";

  // Un precio de venta que se mueve mas de la mitad casi siempre es un costo
  // mal tipeado ($ 5 por $ 2.800 en una compra a mano). No se frena: se pide
  // un tilde. El tilde vale para esta compra; otra arranca sin tildar.
  const preciosQueSaltan = !aplicada && hayPreciosQueSaltan(resumen.cambianPrecio);
  const [preciosConfirmados, setPreciosConfirmados] = useState<string | null>(null);
  const faltaConfirmarPrecios = preciosQueSaltan && preciosConfirmados !== compra.id;
  const faltaConfirmar = faltaConfirmarPrecios || deudaPorConfirmar;

  // Las unidades que cerraron un encargo no quedan en el stock. Antes de
  // aplicar son las tildadas; ya aplicada, las que dice el resultado o la
  // compra. «Unidades» las sumaba todas.
  const unidadesEncargadas = aplicada
    ? (resultado?.encargos_recibidos ?? compra.encargos_recibidos ?? 0)
    : encargosTildados;
  const alStock = Math.max(resumen.unidades - unidadesEncargadas, 0);

  return (
    <div className="space-y-4">
      {aplicada ? (
        <Card tono="success" padding="normal" className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <TituloSeccion>La compra quedó aplicada</TituloSeccion>
              <p className="text-sm text-muted-foreground">
                {resultado
                  ? textoDeLoAplicado(resultado)
                  : referenciaDe(compra) === "Compra"
                    ? "La mercadería de esta compra ya entró."
                    : `La mercadería de ${referenciaDe(compra)} ya entró.`}{" "}
                Los costos de reposición y los precios ya se actualizaron.
              </p>
              {compra.aplicado_at ? (
                <p className="text-xs tabular-nums text-muted-foreground">
                  {formatearFechaHora(compra.aplicado_at)}
                  {compra.lote_id ? (
                    <>
                      {" "}
                      · lote <Codigo>{compra.lote_id.slice(0, 8)}</Codigo>
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmarRevertir(true)}
              cargando={revirtiendo}
            >
              <Undo2 aria-hidden="true" />
              Deshacer
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Deshacer borra los ingresos y devuelve cada costo a lo que valía. También borra la deuda
            y, si se pagó al cargarla, el pago y su movimiento de caja. Los productos que se crearon
            quedan en el catálogo con stock cero.
          </p>
        </Card>
      ) : null}

      <Card padding="normal" className="space-y-4">
        <TituloSeccion>{aplicada ? "Lo que entró" : "Qué va a pasar al aplicar"}</TituloSeccion>

        <dl
          className={
            unidadesEncargadas > 0
              ? "grid grid-cols-2 gap-4 sm:grid-cols-5"
              : "grid grid-cols-2 gap-4 sm:grid-cols-4"
          }
        >
          <Numero titulo="Productos" valor={String(resumen.productos)} />
          <Numero titulo="Unidades al stock" valor={mostrarCantidad(alStock)} />
          {unidadesEncargadas > 0 ? (
            <Numero titulo="Encargos" valor={String(unidadesEncargadas)} />
          ) : null}
          <Numero titulo="Se crean" valor={String(resumen.nuevos)} />
          <Numero titulo="Cambian de precio" valor={String(resumen.cambianPrecio.length)} />
        </dl>

        {resumen.porMoneda.length > 0 ? (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              Lo que vale lo que entra, a costo con IVA
            </p>
            <ul className="flex flex-wrap gap-4">
              {resumen.porMoneda.map((m) => (
                <li key={m.moneda} className="flex items-baseline gap-1">
                  <Importe
                    monto={m.total}
                    moneda={m.moneda}
                    tamano="lg"
                    className="font-semibold"
                  />
                  <span className="text-xs text-muted-foreground">
                    {m.lineas} {m.lineas === 1 ? "producto" : "productos"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {resumen.cambianPrecio.length > 0 ? (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Precios de venta que cambian</p>
            <ul className="divide-y divide-border rounded-md border border-border">
              {resumen.cambianPrecio.map(({ fila, de, a, variacion }) => (
                <li
                  key={fila.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <span className="min-w-0 truncate">{fila.producto_nombre}</span>
                  <span className="flex items-center gap-2">
                    <Importe
                      monto={de}
                      moneda={fila.precio_moneda ?? "ARS"}
                      codigo={false}
                      className="line-through"
                    />
                    <ArrowRight className="size-3 text-muted-foreground" aria-hidden="true" />
                    <Importe
                      monto={a}
                      moneda={fila.precio_moneda ?? "ARS"}
                      className="font-semibold"
                    />
                    <span
                      className={
                        Math.abs(variacion) > SALTO_DE_PRECIO
                          ? "text-xs font-semibold tabular-nums text-warning"
                          : "text-xs tabular-nums text-muted-foreground"
                      }
                    >
                      ({variacion > 0 ? "+" : "−"}
                      {formatearNumero(Math.abs(variacion) * 100, 1)} %)
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            {preciosQueSaltan ? (
              <Aviso tono="warning">
                <div className="space-y-2">
                  <p>
                    Hay precios que cambian más de la mitad. Suele ser un costo mal escrito: volvé a
                    los productos y corregilo con «Otro precio» en esa línea antes de aplicar.
                  </p>
                  <Checkbox
                    label="Lo revisé, los precios están bien"
                    checked={!faltaConfirmarPrecios}
                    onChange={(e) => setPreciosConfirmados(e.target.checked ? compra.id : null)}
                  />
                </div>
              </Aviso>
            ) : null}
          </div>
        ) : null}

        {unidadesQueNoLlegaron > 0 ? (
          <p className="text-xs text-muted-foreground">
            {mostrarCantidad(unidadesQueNoLlegaron)}{" "}
            {unidadesQueNoLlegaron === 1 ? "unidad no llegó" : "unidades no llegaron"}
            {conTotal
              ? ": no entran al stock, pero la deuda es el total de la factura, que las incluye."
              : ": no entran al stock ni suman a la deuda. Si la factura las cobra igual, cargá su total."}
          </p>
        ) : null}

        {!aplicada && lineasEnOtraMoneda > 0 && compra.moneda ? (
          <Aviso tono="warning">
            {lineasEnOtraMoneda === 1
              ? "Un producto está cargado"
              : `${lineasEnOtraMoneda} productos están cargados`}{" "}
            en {compra.moneda === "USD" ? "pesos" : "dólares"} y la compra es en{" "}
            {compra.moneda === "USD" ? "dólares" : "pesos"}
            {conTotal
              ? ": la deuda es el total de la factura, pero el costo de esos productos queda en otra moneda."
              : ": no suman a lo que le debés."}{" "}
            Si el precio de la factura está en {compra.moneda === "USD" ? "dólares" : "pesos"},
            volvé a los productos y corregí la moneda.
          </Aviso>
        ) : null}

        {resumen.afuera > 0 ? (
          <p className="text-xs text-muted-foreground">
            {resumen.afuera} {resumen.afuera === 1 ? "producto queda" : "productos quedan"} afuera
            por decisión tuya.
          </p>
        ) : null}

        {/* Es un bloqueo, no una advertencia: se anuncia como alerta aunque el
            tono sea warning (no esta mal, falta algo). */}
        {!aplicada && !puede.ok ? (
          <Aviso tono="warning" role="alert">
            {puede.motivo}
          </Aviso>
        ) : null}
      </Card>

      {/* La deuda va entre los totales y los botones: es lo último que se decide
          antes de apretar Aplicar. */}
      {encargos}

      {deuda}

      {!aplicada ? (
        <div className="space-y-2">
          {puede.ok && faltaConfirmar ? (
            <p className="text-right text-xs text-muted-foreground">
              {faltaConfirmarPrecios && deudaPorConfirmar
                ? "Falta confirmar arriba los precios que cambian mucho y el importe de la deuda."
                : faltaConfirmarPrecios
                  ? "Falta confirmar arriba los precios que cambian mucho."
                  : "Falta confirmar arriba el importe de la deuda."}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-between gap-2">
            <Button type="button" variant="outline" onClick={onVolver} disabled={aplicando}>
              Volver a los productos
            </Button>
            <Button
              type="button"
              onClick={() => setConfirmarAplicar(true)}
              disabled={!puede.ok || faltaConfirmar}
              cargando={aplicando}
            >
              Aplicar la compra
            </Button>
          </div>
        </div>
      ) : null}

      <Dialog open={confirmarAplicar} onOpenChange={setConfirmarAplicar}>
        <DialogContent tamano="sm">
          <DialogHeader>
            <DialogTitle>Aplicar {referenciaEnFrase(compra, "la")}</DialogTitle>
            <DialogDescription>{textoDeAplicar(resumen, encargosTildados)}</DialogDescription>
          </DialogHeader>
          {deudaAlAplicar ? (
            <p className="text-sm">
              {deudaAlAplicar.conPago ? "Se anota una compra de " : "Queda una deuda de "}
              <Importe
                monto={deudaAlAplicar.importe}
                moneda={deudaAlAplicar.moneda}
                className="font-semibold"
              />{" "}
              {deudaAlAplicar.conPago
                ? `a ${deudaAlAplicar.proveedor}, con el pago que cargaste.`
                : `con ${deudaAlAplicar.proveedor}.`}
              {deudaAlAplicar.conPago && deudaAlAplicar.falta > 0.009 ? (
                <>
                  {" "}
                  El pago no la cubre entera: queda una deuda de{" "}
                  <Importe
                    monto={deudaAlAplicar.falta}
                    moneda={deudaAlAplicar.moneda}
                    className="font-semibold"
                  />
                  .
                </>
              ) : null}
            </p>
          ) : null}
          <p className="text-sm text-muted-foreground">
            Esto escribe los ingresos de stock y actualiza el costo de reposición. Se puede deshacer
            desde esta misma pantalla.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmarAplicar(false)}
              disabled={aplicando}
            >
              Todavía no
            </Button>
            <Button
              type="button"
              disabled={aplicando}
              onClick={() => {
                setConfirmarAplicar(false);
                onAplicar();
              }}
            >
              Sí, aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmarRevertir} onOpenChange={setConfirmarRevertir}>
        <DialogContent tamano="sm">
          <DialogHeader>
            <DialogTitle>Deshacer la compra</DialogTitle>
            <DialogDescription>
              Se borran los ingresos de {referenciaEnFrase(compra, "esta")} y cada costo vuelve a lo
              que valía. También se borran la deuda con el proveedor y, si la pagaste al cargarla,
              ese pago se borra y la plata vuelve a la caja: si la volvés a cargar, cargá el pago de
              nuevo. Los productos creados quedan, con stock cero.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmarRevertir(false)}
              disabled={revirtiendo}
            >
              No, dejarla aplicada
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={revirtiendo}
              onClick={() => {
                setConfirmarRevertir(false);
                onRevertir();
              }}
            >
              Sí, deshacer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Lo que dice la tarjeta despues de aplicar. Las unidades que cerraron un
 * encargo no quedan en el stock: van al cliente que las espera. Antes se
 * contaban todas como stock.
 */
export function textoDeLoAplicado(resultado: ResultadoAplicarCompra): string {
  const encargos = resultado.encargos_recibidos ?? 0;
  const alStock = Math.max(resultado.unidades - encargos, 0);
  // Sin las lineas que fueron enteras a un encargo: con `ingresados` decia «en
  // 2 productos» cuando al stock habia entrado uno solo.
  const enStock = resultado.productos_al_stock ?? resultado.ingresados;
  const productos = `${enStock} ${enStock === 1 ? "producto" : "productos"}`;
  const partes: string[] = [];
  // Si todo fue a encargos, «Entraron 0 unidades al stock en 0 productos» no
  // dice nada: se cuenta solo el encargo.
  if (alStock > 0 || encargos === 0) {
    partes.push(
      `${alStock === 1 ? "Entró" : "Entraron"} ${mostrarCantidad(alStock)} ${alStock === 1 ? "unidad" : "unidades"} al stock en ${productos}`,
    );
  }
  if (encargos > 0) {
    partes.push(`${encargos === 1 ? "se cerró 1 encargo" : `se cerraron ${encargos} encargos`}`);
  }
  if (resultado.creados > 0) {
    partes.push(
      `se ${resultado.creados === 1 ? "creó" : "crearon"} ${resultado.creados} ${resultado.creados === 1 ? "producto" : "productos"}`,
    );
  }
  const texto = partes.join(", ");
  return `${texto.charAt(0).toUpperCase()}${texto.slice(1)}.`;
}

/** Un conteo (productos, unidades): no es plata, va sin color ni chip. */
function Numero({ titulo, valor }: { titulo: string; valor: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{titulo}</dt>
      <dd className="text-2xl font-semibold tabular-nums">{valor}</dd>
    </div>
  );
}
